import React, { useEffect, useState } from 'react';
import { apiConstants, postGeneric } from '../../common/constants';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import { Box, Button, Card, CardContent, Chip, Grid, Typography } from '@mui/material';
import Loader from '../../common/Loader';
import _ from 'lodash';
import { useForm } from 'react-hook-form';
import { FormProvider, RHFSearchableSelectField } from '../../common/hook-form';
import { convertToCSV, downloadCSV, formatCurrency } from '../../helpers/UtilityHelper';
import { formatDate } from '../../common/util';
import { WALLET_TYPE_OPTIONS, WalletTypeChip } from './walletUi';

/**
 * Stranded Wallet Residuals — money left on DEACTIVATED wallets after a Prepaid<->Postpaid
 * switch. These balances appear in no active-wallet report; this view is finance's worklist
 * for settling them (refund vs carry-forward).
 */
export default function StrandedResiduals() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(20);
    const [merchantFilter, setMerchantFilter] = useState('');
    const [walletTypeFilter, setWalletTypeFilter] = useState('');

    const methods = useForm({ defaultValues: { merchant: '', walletType: '' } });
    const { reset } = methods;

    useEffect(() => {
        setLoading(true);
        postGeneric(apiConstants.REPORT_GENERIC_SEARCH, {
            collection: 'merchant_wallet',
            limit: 10000,
            andfilter: [{ field: 'isActive', operator: 'eq', value: false }],
        }).then((res) => {
            // drop malformed stub docs (no merchantRef/walletType) before anything else
            const all = (res?.data?.data || []).filter((w) => w.merchantRef && w.walletType);
            // only wallets that still carry money: a balance (either sign) or an outstanding due
            const stranded = all.filter((w) =>
                Math.round(w.walletBalance || 0) !== 0 || Math.round(w.dueAmount || 0) !== 0);
            setRows(_.orderBy(stranded, (w) => Math.abs(w.walletBalance || 0) + Math.abs(w.dueAmount || 0), ['desc']));
        }).catch((err) => {
            console.error(err);
            setRows([]);
        }).finally(() => setLoading(false));
    }, []);

    const filteredRows = rows.filter((w) =>
        (!merchantFilter || w.merchantRef === merchantFilter) &&
        (!walletTypeFilter || w.walletType === walletTypeFilter)
    );

    // dropdown lists only merchants that actually have a residual
    const merchantOptions = _.orderBy(
        _.uniqBy(rows.map((w) => ({ value: w.merchantRef, label: w.merchantName || w.merchantRef })), 'value'),
        'label'
    );

    // stats follow the applied filter. Type-aware on purpose: a wallet contributes only its own
    // type's balance field, so a hypothetical postpaid doc carrying a mirrored negative
    // walletBalance can never be double-counted (verified none exist in prod today — this is
    // insurance). A NEGATIVE due (merchant overpaid their postpaid dues) counts as merchant cash.
    const owedToProzo = _.sumBy(filteredRows, (w) =>
        w.walletType === 'Prepaid'
            ? (w.walletBalance < 0 ? Math.abs(w.walletBalance) : 0)
            : (w.dueAmount > 0 ? w.dueAmount : 0));
    const merchantCash = _.sumBy(filteredRows, (w) =>
        w.walletType === 'Prepaid'
            ? (w.walletBalance > 0 ? w.walletBalance : 0)
            : (w.dueAmount < 0 ? Math.abs(w.dueAmount) : 0));

    const onSubmit = (data) => {
        setMerchantFilter(data.merchant || '');
        setWalletTypeFilter(data.walletType || '');
        setPage(0);
    };

    const columns = [
        { field: 'merchantName', headerName: 'Merchant', flex: 1.6, minWidth: 200 },
        { field: 'merchantEmail', headerName: 'Email', flex: 1.6, minWidth: 220 },
        {
            field: 'walletType', headerName: 'Dead Wallet', flex: 1, minWidth: 150, align: 'center', headerAlign: 'center',
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <WalletTypeChip type={row.walletType} />
                    <Chip size="small" label="Inactive" />
                </Box>
            ),
        },
        {
            field: 'walletBalance', headerName: 'Residual Balance', flex: 1, minWidth: 140, align: 'right', headerAlign: 'right',
            renderCell: ({ row }) => row.walletBalance == null ? '—' : (
                <span style={{ fontWeight: 600, color: row.walletBalance < 0 ? '#dc3545' : '#28a745' }}>
                    {formatCurrency(row.walletBalance)}
                </span>
            ),
        },
        {
            field: 'dueAmount', headerName: 'Outstanding Due', flex: 1, minWidth: 140, align: 'right', headerAlign: 'right',
            renderCell: ({ row }) => row.dueAmount == null ? '—' : (
                <span style={{ fontWeight: 600, color: row.dueAmount > 0 ? '#dc3545' : 'inherit' }}>
                    {formatCurrency(row.dueAmount)}
                </span>
            ),
        },
        {
            field: 'updatedAt', headerName: 'Last Updated', flex: 1, minWidth: 140, align: 'center', headerAlign: 'center',
            renderCell: ({ row }) => formatDate(row.updatedAt || row.createdAt),
        },
    ];

    const handleExport = () => {
        // merchant names can contain commas and the CSV helper does not quote fields
        const csv = convertToCSV(filteredRows.map((w) => ({
            ...w,
            merchantName: (w.merchantName || '').replace(/,/g, ';'),
            walletBalance: Math.round(w.walletBalance || 0),
            dueAmount: Math.round(w.dueAmount || 0),
        })), [
            { label: 'Merchant', key: 'merchantName' },
            { label: 'Email', key: 'merchantEmail' },
            { label: 'Dead Wallet Type', key: 'walletType' },
            { label: 'Residual Balance', key: 'walletBalance' },
            { label: 'Outstanding Due', key: 'dueAmount' },
        ]);
        downloadCSV(csv, 'stranded_wallet_residuals.csv');
    };

    const statCard = (label, value, color) => (
        <Card variant="outlined" sx={{ minWidth: 220 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h6" sx={{ color, fontWeight: 700 }}>{value}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
            </CardContent>
        </Card>
    );

    return (
        <>
            {loading && <Loader />}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
                        <Grid container spacing={1} sx={{ display: 'flex', justifyContent: 'left' }}>
                            <Grid item xs={12} md={5}>
                                <RHFSearchableSelectField
                                    width="100%"
                                    name="merchant"
                                    label="Select Merchant"
                                    options={merchantOptions}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <RHFSearchableSelectField
                                    width="100%"
                                    name="walletType"
                                    label="Select Wallet Type"
                                    options={WALLET_TYPE_OPTIONS}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Button type="submit" variant="contained" color="primary" size="small" sx={{ mt: 2 }}>
                                    Apply Filters
                                </Button>
                                <Button variant="contained" color="error" size="small" sx={{ mt: 2, ml: 1 }}
                                    onClick={() => {
                                        setMerchantFilter('');
                                        setWalletTypeFilter('');
                                        setPage(0);
                                        reset({ merchant: '', walletType: '' });
                                    }}>
                                    Reset
                                </Button>
                                <Button variant="contained" color="secondary" size="small" sx={{ mt: 2, ml: 1 }}
                                    onClick={handleExport} disabled={!filteredRows.length}>
                                    Export CSV
                                </Button>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </CardContent>
            </Card>
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
                <Grid item>{statCard('Owed to Prozo (on dead wallets)', formatCurrency(owedToProzo), '#dc3545')}</Grid>
                <Grid item>{statCard('Merchant cash stranded', formatCurrency(merchantCash), '#28a745')}</Grid>
                <Grid item>{statCard('Wallets with residual', String(filteredRows.length), 'inherit')}</Grid>
            </Grid>
            <Card>
                <CardContent sx={{ p: 0 }}>
                    <ProzoDataGrid
                        autoHeight={true}
                        columns={columns}
                        rows={filteredRows}
                        getRowId={(row) => `${row.merchantRef}_${row.walletType}`}
                        sizePerPage={sizePerPage}
                        setSizePerPage={setSizePerPage}
                        setPage={setPage}
                        page={page}
                        paginationMode="client"
                        rowCount={filteredRows.length}
                    />
                </CardContent>
            </Card>
        </>
    );
}
